import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUploadPhoto } from '../../hooks/usePhotos';
import API_URL from '../../config';
import './UploadPhotoModal.css';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE = 5 * 1024 * 1024;

function getAuthHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function UploadPhotoModal({ onClose, defaultReservationId = null }) {
  const { user } = useAuth();
  const uploadPhoto = useUploadPhoto();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [reservationId, setReservationId] = useState(defaultReservationId || '');
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/user-groups`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => setGroups(d.data || []))
      .catch(() => {});

    fetch(`${API_URL}/api/reservations?status=approved`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => setReservations(d.data || d.reservations || d || []))
      .catch(() => {});
  }, []);

  // If opened from a reservation, lock visibility to reservation
  useEffect(() => {
    if (defaultReservationId) {
      setVisibility('reservation');
      setReservationId(String(defaultReservationId));
    }
  }, [defaultReservationId]);

  function handleFileSelect(selected) {
    setError('');
    if (!selected) return;
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setError('Please choose a JPEG, PNG, WEBP, or GIF image.');
      return;
    }
    if (selected.size > MAX_SIZE) {
      setError('Image must be under 5MB.');
      return;
    }
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { setError('Please select an image.'); return; }
    if (visibility === 'group' && !groupId) { setError('Please select a group.'); return; }
    if (visibility === 'reservation' && !reservationId) { setError('Please select a reservation.'); return; }
    if (visibility === 'family' && !user.family) { setError('Your account has no family set.'); return; }

    setError('');
    try {
      await uploadPhoto.mutateAsync({
        file,
        photoType: 'gallery',
        visibility,
        caption: caption.trim() || undefined,
        reservationId: visibility === 'reservation' ? parseInt(reservationId, 10) : undefined,
        groupId: visibility === 'group' ? parseInt(groupId, 10) : undefined,
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed. Please try again.');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={e => e.stopPropagation()}>
        <div className="upload-modal-header">
          <h2>Share a Photo</h2>
          <button className="modal-close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="upload-modal-form">
          {/* Drop zone */}
          <div
            className={`drop-zone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {preview ? (
              <img src={preview} alt="Preview" className="drop-zone-preview" />
            ) : (
              <div className="drop-zone-placeholder">
                <ImageIcon size={40} />
                <p>Drag &amp; drop or <span>browse</span></p>
                <p className="drop-zone-hint">JPEG, PNG, WEBP, GIF · Max 5MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => handleFileSelect(e.target.files[0])}
            />
          </div>

          {/* Caption */}
          <div className="form-group">
            <label htmlFor="caption">Caption (optional)</label>
            <input
              id="caption"
              type="text"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Add a caption…"
              maxLength={300}
            />
          </div>

          {/* Visibility — hide selector if opened from a reservation */}
          {!defaultReservationId && (
            <div className="form-group">
              <label>Who can see this?</label>
              <div className="visibility-options">
                <label className={`visibility-option ${visibility === 'public' ? 'selected' : ''}`}>
                  <input type="radio" name="visibility" value="public" checked={visibility === 'public'} onChange={() => setVisibility('public')} />
                  <strong>Public</strong>
                  <span>All users</span>
                </label>

                {user.family && (
                  <label className={`visibility-option ${visibility === 'family' ? 'selected' : ''}`}>
                    <input type="radio" name="visibility" value="family" checked={visibility === 'family'} onChange={() => setVisibility('family')} />
                    <strong>Family</strong>
                    <span>{user.family}</span>
                  </label>
                )}

                <label className={`visibility-option ${visibility === 'owners' ? 'selected' : ''}`}>
                  <input type="radio" name="visibility" value="owners" checked={visibility === 'owners'} onChange={() => setVisibility('owners')} />
                  <strong>Owners</strong>
                  <span>All property owners</span>
                </label>

                {groups.length > 0 && (
                  <label className={`visibility-option ${visibility === 'group' ? 'selected' : ''}`}>
                    <input type="radio" name="visibility" value="group" checked={visibility === 'group'} onChange={() => setVisibility('group')} />
                    <strong>Group</strong>
                    <span>A specific group</span>
                  </label>
                )}

                <label className={`visibility-option ${visibility === 'reservation' ? 'selected' : ''}`}>
                  <input type="radio" name="visibility" value="reservation" checked={visibility === 'reservation'} onChange={() => setVisibility('reservation')} />
                  <strong>Reservation</strong>
                  <span>Trip participants only</span>
                </label>
              </div>
            </div>
          )}

          {/* Group picker */}
          {visibility === 'group' && (
            <div className="form-group">
              <label htmlFor="groupId">Select group</label>
              <select id="groupId" value={groupId} onChange={e => setGroupId(e.target.value)} required>
                <option value="">Choose a group…</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Reservation picker */}
          {visibility === 'reservation' && !defaultReservationId && (
            <div className="form-group">
              <label htmlFor="reservationId">Select reservation</label>
              <select id="reservationId" value={reservationId} onChange={e => setReservationId(e.target.value)} required>
                <option value="">Choose a reservation…</option>
                {reservations.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.reservation_number || `#${r.id}`}{r.check_in ? ` — ${r.check_in}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && <p className="upload-error">{error}</p>}

          <div className="upload-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploadPhoto.isPending || !file}>
              {uploadPhoto.isPending ? (
                <><span className="btn-spinner" /> Uploading…</>
              ) : (
                <><Upload size={16} /> Share Photo</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
